// Navigation history behind the spotlight's recommendations.
//
// Every authenticated route change is recorded (see the `router.afterEach`
// hook in src/router/index.js), so the "Recent" and "Suggested" groups reflect
// how the user actually moves through the app — not just what they opened via
// the spotlight itself. Everything lives in localStorage; there is no API call
// anywhere in this file, which is what keeps opening the palette instant.

const STORAGE_KEY = 'oms_spotlight_usage'
const MAX_TRACKED_PATHS = 120
const MAX_RECENT = 12

// Paths that must never be recommended back to the user.
const IGNORED_PREFIXES = ['/login', '/register', '/pages/']

const HOUR = 60 * 60 * 1000
const DAY = 24 * HOUR

// In-memory mirror so a keystroke never touches localStorage. Written back on
// each visit (a handful of writes per session, all off the render path).
let cache = null

function emptyState() {
  return { counts: {}, recent: [] }
}

function read() {
  if (cache) {
    return cache
  }

  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : null

    cache = parsed && typeof parsed === 'object'
      ? {
        counts: parsed.counts && typeof parsed.counts === 'object' ? parsed.counts : {},
        recent: Array.isArray(parsed.recent) ? parsed.recent.filter((p) => typeof p === 'string') : [],
      }
      : emptyState()
  } catch (error) {
    cache = emptyState()
  }

  return cache
}

function write(state) {
  cache = state

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch (error) {
    // Storage full or blocked — recommendations degrade to the in-memory
    // mirror for this tab, which is a fine outcome for a convenience feature.
  }
}

// Keep the store from growing without bound: once it passes the cap, drop the
// coldest entries (fewest visits, oldest last-seen) rather than clearing.
function prune(counts) {
  const paths = Object.keys(counts)

  if (paths.length <= MAX_TRACKED_PATHS) {
    return counts
  }

  const kept = paths
    .sort((a, b) => (counts[b].n - counts[a].n) || (counts[b].t - counts[a].t))
    .slice(0, MAX_TRACKED_PATHS)

  const next = {}
  kept.forEach((path) => { next[path] = counts[path] })
  return next
}

export function isTrackablePath(path) {
  if (typeof path !== 'string' || !path.startsWith('/') || path === '/') {
    return false
  }

  return !IGNORED_PREFIXES.some((prefix) => path === prefix || path.startsWith(prefix))
}

/**
 * Record a page visit. Keyed on `route.path` (no query string) so the same
 * page reached with different filters still accumulates one score.
 *
 * @param {string} path
 */
export function recordVisit(path) {
  if (!isTrackablePath(path)) {
    return
  }

  const state = read()
  const entry = state.counts[path] || { n: 0, t: 0 }

  const counts = { ...state.counts, [path]: { n: entry.n + 1, t: Date.now() } }
  const recent = [path, ...state.recent.filter((p) => p !== path)].slice(0, MAX_RECENT)

  write({ counts: prune(counts), recent })
}

/**
 * Most-recently-visited paths, newest first.
 *
 * @returns {string[]}
 */
export function getRecentPaths() {
  return read().recent.slice()
}

/**
 * Affinity score for a path: visit count, weighted by how recently it was
 * last opened. A page visited 40 times last month should not outrank the one
 * visited 8 times this morning.
 *
 * @param {string} path
 * @returns {number} 0 when the path has never been visited
 */
export function getUsageScore(path) {
  const entry = read().counts[path]

  if (!entry) {
    return 0
  }

  const age = Date.now() - (entry.t || 0)
  let recency = 4
  if (age < HOUR) recency = 40
  else if (age < DAY) recency = 26
  else if (age < 7 * DAY) recency = 14
  else if (age < 30 * DAY) recency = 8

  return Math.min(entry.n, 30) * 4 + recency
}

export function clearUsage() {
  write(emptyState())
}
